/**
 * Markup de la pantalla `/logbook.html` — Fase 6b-UI-B.
 *
 * Estructura:
 *
 *   .logbook-stage
 *     .logbook-notebook              (background image del cuaderno)
 *       .logbook-page--left          (REGISTRO DE PILOTOS + tabla)
 *       .logbook-page--right         (INFORMACIÓN — placeholder o preview)
 *
 * El boli decorativo del reporte NO se reutiliza aquí (es del módulo
 * `report-upload`). Si más adelante queremos un detalle equivalente, lo
 * añadiremos como hijo del `.logbook-stage`.
 */

import type { PilotStatus, PilotSummary } from '@51esc/shared';
import {
  avatarUrl,
  escapeHtml,
  formatHoursDecimal,
  statusLabel,
} from './helpers.js';

/**
 * Claves de ordenación de la tabla. Coinciden con campos de
 * `PilotSummary` que sí queremos exponer al usuario.
 */
export type SortKey =
  | 'callsign'
  | 'rank'
  | 'status'
  | 'totalMissions'
  | 'totalKills'
  | 'totalKillsGround'
  | 'totalHoursDecimal';

export type SortDir = 'asc' | 'desc';

export interface SortState {
  key: SortKey;
  dir: SortDir;
}

interface ColumnDef {
  key: SortKey;
  label: string;
}

/**
 * Columnas en orden visual. El sortKey identifica el campo a ordenar
 * y la label es lo que se pinta en el header.
 */
const COLUMNS: ColumnDef[] = [
  { key: 'callsign', label: 'CALLSIGN' },
  { key: 'rank', label: 'RANGO' },
  { key: 'status', label: 'ESTADO' },
  { key: 'totalMissions', label: 'MISIONES' },
  { key: 'totalKills', label: 'A/A' },
  { key: 'totalKillsGround', label: 'A/G' },
  { key: 'totalHoursDecimal', label: 'HORAS' },
];

/**
 * Inner HTML del header de la tabla con el estado de ordenación
 * actual. Útil para repintar SOLO las flechas/clases activas cuando
 * cambia el sort, sin recrear el grid entero.
 */
export function renderHeaderRow(sort: SortState): string {
  return COLUMNS.map((c) => renderHeaderCell(c, sort)).join('');
}

/** HTML inicial del cuaderno con la tabla vacía + placeholder dcho. */
export function renderLogbookListShell(sort: SortState): string {
  return `
    <div class="logbook-stage">
      <div class="logbook-notebook" role="region" aria-label="Logbook">
        <img
          class="logbook-notebook-bg"
          src="/logbook/notebook.png"
          alt=""
          aria-hidden="true"
        />
        <section class="logbook-page logbook-page--left">
          <h1 class="logbook-page-title">REGISTRO DE PILOTOS</h1>
          <div class="logbook-table" role="grid">
            <div class="logbook-table-header" role="row">
              ${renderHeaderRow(sort)}
            </div>
            <ul class="logbook-table-body" id="logbook-pilots" role="rowgroup">
              <li class="logbook-row logbook-row--loading" role="row">
                <span role="gridcell">cargando…</span>
              </li>
            </ul>
          </div>
        </section>

        <section class="logbook-page logbook-page--right">
          <h1 class="logbook-page-title">INFORMACIÓN</h1>
          <div class="logbook-info" id="logbook-info" data-state="empty">
            ${renderInfoPlaceholder()}
          </div>
        </section>
      </div>
    </div>
  `;
}

/**
 * Renderiza una celda de cabecera como botón con flecha de ordenación.
 * El botón siempre tiene un `<span class="logbook-sort-arrow">` aunque
 * la columna no esté activa, para reservar espacio y que la flecha
 * aparezca/desaparezca sin reflows de la cabecera.
 */
function renderHeaderCell(col: ColumnDef, sort: SortState): string {
  const isActive = sort.key === col.key;
  // Convención visual: `⇅` columnas inactivas, `▲/▼` la activa.
  // Misma flecha que `panel-controls.ts` usa en la ficha del piloto.
  const arrow = isActive ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅';
  const ariaSort = isActive
    ? sort.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  return `
    <button
      type="button"
      role="columnheader"
      class="logbook-th${isActive ? ' logbook-th--active' : ''}"
      data-sort-key="${col.key}"
      aria-sort="${ariaSort}"
    >
      <span class="logbook-th-label">${col.label}</span>
      <span class="logbook-th-arrow" aria-hidden="true">${arrow}</span>
    </button>
  `;
}

/**
 * Dirección por defecto al activar una columna por primera vez:
 * textos en ascendente, numéricos en descendente (suele ser lo que
 * el usuario espera al pulsar "MISIONES" — ver al más activo arriba).
 */
export function defaultDirFor(key: SortKey): SortDir {
  if (
    key === 'totalMissions' ||
    key === 'totalKills' ||
    key === 'totalKillsGround' ||
    key === 'totalHoursDecimal'
  ) {
    return 'desc';
  }
  return 'asc';
}

/**
 * Ordena una lista de pilotos según `SortState`. Es un sort estable
 * (Array.prototype.sort lo es desde ES2019) sobre una copia, así que
 * el caller puede pasar el original sin mutarlo.
 */
export function sortPilots(
  pilots: PilotSummary[],
  sort: SortState,
): PilotSummary[] {
  const factor = sort.dir === 'asc' ? 1 : -1;
  const copy = pilots.slice();
  copy.sort((a, b) => {
    const va = a[sort.key];
    const vb = b[sort.key];
    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * factor;
    }
    return String(va).localeCompare(String(vb), 'es', {
      sensitivity: 'base',
    }) * factor;
  });
  return copy;
}

/** Placeholder con silueta cuando no hay piloto seleccionado. */
export function renderInfoPlaceholder(): string {
  return `
    <div class="logbook-info-placeholder">
      <div class="logbook-info-silhouette" aria-hidden="true"></div>
      <p class="logbook-info-hint">
        Seleccione un piloto<br />
        para consultar<br />
        su ficha personal
      </p>
    </div>
  `;
}

/** Preview lateral (página derecha) cuando hay un piloto seleccionado. */
export function renderInfoPreview(p: PilotSummary): string {
  const safeCallsign = escapeHtml(p.callsign);
  const safeRank = escapeHtml(p.rank);
  const safeStatus = escapeHtml(statusLabel(p.status));
  // Backend resuelve `avatarUrl` con prioridad sobre preset id. Fallback
  // al helper por compat si el campo no viaja.
  const safeAvatar = escapeHtml(p.avatarUrl ?? avatarUrl(p.avatar));
  const motto = p.motto?.trim();
  // El lema se renderiza como HERMANO del bloque preview (no hijo) para
  // que el `space-evenly` del contenedor `.logbook-info` reparta el
  // hueco vertical entre el cartel "INFORMACIÓN" y la foto: el lema
  // queda centrado en ese espacio cuando existe, y desaparece sin
  // ocupar sitio cuando el piloto no tiene lema.
  const quoteHtml = motto
    ? `<p class="logbook-info-quote">— ${escapeHtml(motto)} —</p>`
    : '';
  return `
    ${quoteHtml}
    <div class="logbook-info-preview">
      <img
        class="logbook-info-avatar"
        src="${safeAvatar}"
        alt="Avatar de ${safeCallsign}"
      />
      <h2 class="logbook-info-callsign">${safeCallsign}</h2>
      <p class="logbook-info-row">
        <span class="logbook-info-key">RANGO</span>
        <span class="logbook-info-val">${safeRank}</span>
      </p>
      <p class="logbook-info-row">
        <span class="logbook-info-key">ESTADO</span>
        <span class="logbook-info-val" data-status="${p.status}">${safeStatus}</span>
      </p>
      <p class="logbook-info-row">
        <span class="logbook-info-key">MISIONES</span>
        <span class="logbook-info-val">${String(p.totalMissions)}</span>
      </p>
      <p class="logbook-info-row">
        <span class="logbook-info-key">A/A</span>
        <span class="logbook-info-val">${String(p.totalKills)}</span>
      </p>
      <p class="logbook-info-row">
        <span class="logbook-info-key">A/G</span>
        <span class="logbook-info-val">${String(p.totalKillsGround)}</span>
      </p>
      <p class="logbook-info-row">
        <span class="logbook-info-key">HORAS</span>
        <span class="logbook-info-val">${formatHoursDecimal(p.totalHoursDecimal)}</span>
      </p>
    </div>
  `;
}

/** Una fila de la tabla. */
export function renderPilotRow(p: PilotSummary, isActive: boolean): string {
  const safeCallsign = escapeHtml(p.callsign);
  const safeRank = escapeHtml(p.rank);
  const safeStatus = escapeHtml(statusLabel(p.status));
  const activeClass = isActive ? ' logbook-row--active' : '';
  return `
    <li
      class="logbook-row${activeClass}"
      role="row"
      data-callsign="${safeCallsign}"
      data-status="${p.status}"
      tabindex="0"
    >
      <span role="gridcell" class="logbook-cell logbook-cell--callsign">${safeCallsign}</span>
      <span role="gridcell" class="logbook-cell">${safeRank}</span>
      <span role="gridcell" class="logbook-cell logbook-cell--status" data-status="${p.status}">${safeStatus}</span>
      <span role="gridcell" class="logbook-cell logbook-cell--num">${String(p.totalMissions)}</span>
      <span role="gridcell" class="logbook-cell logbook-cell--num">${String(p.totalKills)}</span>
      <span role="gridcell" class="logbook-cell logbook-cell--num">${String(p.totalKillsGround)}</span>
      <span role="gridcell" class="logbook-cell logbook-cell--num">${formatHoursDecimal(p.totalHoursDecimal)}</span>
    </li>
  `;
}

/** Mensaje cuando la lista llega vacía. */
export function renderEmptyRow(): string {
  return `
    <li class="logbook-row logbook-row--empty" role="row">
      <span role="gridcell">— sin pilotos verificados todavía —</span>
    </li>
  `;
}

/** Mensaje de error al cargar. */
export function renderErrorRow(message: string): string {
  return `
    <li class="logbook-row logbook-row--error" role="row">
      <span role="gridcell">${escapeHtml(message)}</span>
    </li>
  `;
}

/**
 * Sidebar derecho de acciones. VER FICHA solo activo cuando hay piloto
 * seleccionado. FILTRAR es un toggle que muestra/oculta el sidebar
 * de filtros (igual patrón que en la ficha del piloto). Los botones
 * ORDENAR y EXPORTAR se eliminaron — la ordenación se hace inline
 * en los headers de la tabla.
 */
export function renderActions(): string {
  return `
    <section class="chrome-actions logbook-actions">
      <h2 class="chrome-actions-title">ACCIONES</h2>
      <ul class="logbook-actions-list">
        <li>
          <button
            type="button"
            class="logbook-action-btn"
            id="logbook-action-view"
            disabled
            aria-disabled="true"
          >VER FICHA</button>
        </li>
        <li>
          <button
            type="button"
            class="logbook-action-btn"
            id="logbook-action-filters"
            aria-pressed="false"
          >FILTRAR</button>
        </li>
      </ul>
    </section>
  `;
}

/**
 * Sidebar de filtros del listado (debajo de ACCIONES). Misma carcasa
 * que la ficha del piloto (`renderFiltersSidebar` de markup-piloto)
 * para reusar CSS y patrón visual, pero el contenido (`[data-filters]`)
 * lo rellena `buildPilotsFilters` específico de este listado.
 */
export function renderListFiltersSidebar(): string {
  return `
    <section class="chrome-actions logbook-sidebar-filters" data-filters-sidebar hidden>
      <h2 class="chrome-actions-title">FILTROS</h2>
      <div class="logbook-sidebar-filters-content" data-filters></div>
    </section>
  `;
}

/**
 * Construye el HTML de los filtros del listado de pilotos:
 *  - Enum: RANGO + ESTADO con valores únicos.
 *  - Intervalo: MISIONES, KILLS y HORAS con dos inputs (min/max)
 *    cada uno. Los placeholders muestran el rango actual del
 *    dataset para orientar al usuario.
 *  - Contador "MOSTRANDO X/Y" + botón LIMPIAR.
 *
 * Pensado para inyectar en `[data-filters]` con `innerHTML`.
 */
export function buildPilotsFilters(pilots: PilotSummary[]): string {
  const ranks = uniqueSorted(pilots.map((p) => p.rank));
  // `statuses` viene del map sobre `p.status` (`PilotStatus`), pero el
  // helper `uniqueSorted` lo aplana a string[]. Re-tipamos al iterar
  // para que `statusLabel` lo acepte sin un cast suelto.
  const statuses = uniqueSorted(pilots.map((p) => p.status)) as PilotStatus[];
  const total = pilots.length;

  const rankOpts = ranks
    .map((v) => `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`)
    .join('');
  const statusOpts = statuses
    .map(
      (v) =>
        `<option value="${escapeHtml(v)}">${escapeHtml(statusLabel(v))}</option>`,
    )
    .join('');

  const missionsRange = numericRange(pilots.map((p) => p.totalMissions));
  const killsRange = numericRange(pilots.map((p) => p.totalKills));
  const killsGroundRange = numericRange(pilots.map((p) => p.totalKillsGround));
  const hoursRange = numericRange(pilots.map((p) => p.totalHoursDecimal));

  return `
    <div class="logbook-filter-field">
      <label>RANGO</label>
      <select data-filter-col="rank">
        <option value="">— todos —</option>
        ${rankOpts}
      </select>
    </div>
    <div class="logbook-filter-field">
      <label>ESTADO</label>
      <select data-filter-col="status">
        <option value="">— todos —</option>
        ${statusOpts}
      </select>
    </div>
    ${renderRangeFilter('MISIONES', 'totalMissions', missionsRange, 1)}
    ${renderRangeFilter('A/A', 'totalKills', killsRange, 1)}
    ${renderRangeFilter('A/G', 'totalKillsGround', killsGroundRange, 1)}
    ${renderRangeFilter('HORAS', 'totalHoursDecimal', hoursRange, 0.1)}
    <div class="logbook-filters-actions">
      <span class="logbook-filters-count" data-filters-count>
        MOSTRANDO <span data-filters-visible>${total}</span>/<span data-filters-total>${total}</span>
      </span>
      <button type="button" class="logbook-filters-clear" data-filter-action="clear">LIMPIAR</button>
    </div>
  `;
}

/**
 * Pinta un filtro de intervalo numérico con dos `<input type="number">`
 * (mín / máx). Los placeholders muestran el rango real del dataset
 * para que el usuario sepa los límites sin tener que probar.
 */
function renderRangeFilter(
  label: string,
  col: 'totalMissions' | 'totalKills' | 'totalKillsGround' | 'totalHoursDecimal',
  range: { min: number; max: number },
  step: number,
): string {
  const minPh = formatNumber(range.min);
  const maxPh = formatNumber(range.max);
  return `
    <div class="logbook-filter-field logbook-filter-field--range">
      <label>${escapeHtml(label)}</label>
      <div class="logbook-filter-range-row">
        <input
          type="number"
          inputmode="decimal"
          step="${step}"
          min="0"
          placeholder="${escapeHtml(minPh)}"
          data-filter-range="${col}"
          data-filter-bound="min"
          aria-label="${escapeHtml(label)} mínimo"
        />
        <span class="logbook-filter-range-sep" aria-hidden="true">–</span>
        <input
          type="number"
          inputmode="decimal"
          step="${step}"
          min="0"
          placeholder="${escapeHtml(maxPh)}"
          data-filter-range="${col}"
          data-filter-bound="max"
          aria-label="${escapeHtml(label)} máximo"
        />
      </div>
    </div>
  `;
}

function numericRange(values: number[]): { min: number; max: number } {
  if (values.length === 0) return { min: 0, max: 0 };
  let min = values[0]!;
  let max = values[0]!;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return { min, max };
}

function formatNumber(n: number): string {
  // Para el placeholder del input: enteros sin decimales, decimales con 1.
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b, 'es'));
}
